import { useCallback, useEffect, useRef, useState } from 'react';

import type { ChatMessage } from '../types';
import {
  createUser,
  endSession,
  gatherInfo,
  generateCode,
  improveCode,
} from '../utils/api/llm';

export const STATUS = {
  INITIAL: 'INITIAL',
  PENDING: 'PENDING',
  IDLE: 'IDLE',
  ERROR: 'ERROR',
  REJECTED: 'REJECTED',
} as const;

export type Status = (typeof STATUS)[keyof typeof STATUS];

const getStatusVariants = (currentStatus: Status) => ({
  isInitial: currentStatus === STATUS.INITIAL,
  isPending: currentStatus === STATUS.PENDING,
  isIdle: currentStatus === STATUS.IDLE,
  isError: currentStatus === STATUS.ERROR,
  isRejected: currentStatus === STATUS.REJECTED,
});

interface UseChatOptions {
  initialMessages: ChatMessage[];
  initialSuggestions: string[];
  sessionId: string | null;
  isAuthenticated: boolean;
}

export const useChat = ({
  initialMessages,
  initialSuggestions,
  sessionId,
  isAuthenticated,
}: UseChatOptions) => {
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [code, setCode] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>(initialSuggestions);
  const [initialCodeGenerated, setInitialCodeGenerated] = useState(false);
  const [status, setStatus] = useState<Status>(STATUS.INITIAL);
  const [, setImproveRequestCount] = useState(0);

  const conversationIdRef = useRef(sessionId);

  const handleEndSession = async () => {
    if (!conversationIdRef.current) return;

    try {
      await endSession({ user_id: conversationIdRef.current });
    } catch (error) {
      setStatus(STATUS.ERROR);
    }
  };

  const sendPrompt = async (input: string) => {
    setMessages((state) => [...state, { kind: 'USER_MESSAGE', text: input }]);
    setSuggestions([]);
    setStatus(STATUS.PENDING);

    try {
      const userId = await getUserId();

      if (!initialCodeGenerated) {
        await handleGenerateInitialCode(userId, input);
      } else {
        await handleImproveCode(userId, input);
      }

      setStatus(STATUS.IDLE);
    } catch (error) {
      setStatus(STATUS.ERROR);
    }
  };

  const getUserId = useCallback(async () => {
    if (!sessionId || !conversationIdRef.current) {
      const { data } = await createUser();
      conversationIdRef.current = data.user_id;
      return data.user_id;
    }

    return conversationIdRef.current;
  }, [sessionId]);

  const handleGenerateInitialCode = async (userId: string, input: string) => {
    const { data } = await gatherInfo({
      user_id: userId,
      user_input: input,
    });

    setMessages((state) => [
      ...state,
      { kind: 'BOT_MESSAGE', text: data.llm_message },
    ]);
    setSuggestions(data.suggested_answers);

    if (data.finished_info_gathering) {
      await initializeCode(userId);
      setInitialCodeGenerated(true);
    }
  };

  const initializeCode = async (userId: string) => {
    const { data } = await generateCode({ user_id: userId });

    setMessages((state) => [
      ...state,
      { kind: 'BOT_MESSAGE', text: data.llm_message },
    ]);
    setSuggestions(data.suggested_improvements);
    setCode(data.code);
  };

  const handleImproveRequestReject = useCallback(
    async (requestCount: number) => {
      if (isAuthenticated || requestCount < 2) return;

      await handleEndSession();
      conversationIdRef.current = null;
      setStatus(STATUS.REJECTED);
    },
    [isAuthenticated, handleEndSession],
  );

  const handleImproveCode = async (userId: string, input: string) => {
    const { data: improved } = await improveCode({
      user_id: userId,
      user_input: input,
    });

    setMessages((state) => [
      ...state,
      { kind: 'BOT_MESSAGE', text: improved.llm_message },
    ]);
    setSuggestions(improved.suggested_improvements);
    setCode(improved.code);

    setImproveRequestCount((count) => {
      const newCount = count + 1;
      handleImproveRequestReject(newCount);
      return newCount;
    });
  };

  return {
    conversationId: conversationIdRef.current,
    messages,
    setMessages,
    suggestions,
    setSuggestions,
    code,
    setCode,
    status: getStatusVariants(status),
    sendPrompt,
  };
};
